import { ExploreOccupant, ExploreRoom, RankedRoom } from '../services/api/exploreService';

/**
 * News-front-page grammar for the rooms/explore screens (V2 Amethyst WU-03).
 * Deterministic, client-side, no LLM — see rooms-screen-news-layout.md §3.
 * Shared by RoomsDashboard.tsx and ExplorePage.tsx so both screens read the
 * same headline/byline/section rules.
 */

type NewsRoom = ExploreRoom | RankedRoom;

// ---- kind/category verb + label tables (R3: deterministic headline templates) ----

const KIND_PHRASE: Record<string, (world: string) => string> = {
  party: (w) => `partying in ${w}`,
  hunt: (w) => `on a scavenger hunt in ${w}`,
  maze: (w) => `lost in the maze at ${w}`,
  escape: (w) => `mid-escape in ${w}`,
  race: (w) => `racing through ${w}`,
  quiz: (w) => `deep in trivia night at ${w}`,
  connect: (w) => `getting to know each other in ${w}`,
  decode: (w) => `solving puzzles together in ${w}`,
  rts: (w) => `commanding armies in ${w}`,
};

const CATEGORY_PHRASE: Record<string, (world: string) => string> = {
  games: (w) => `playing in ${w}`,
  lobbies: (w) => `hanging out in ${w}`,
  interests: (w) => `talking shop in ${w}`,
  dating: (w) => `getting to know each other in ${w}`,
};

export const KIND_LABEL: Record<string, string> = {
  party: 'Party',
  hunt: 'Hunt',
  maze: 'Maze',
  escape: 'Escape',
  race: 'Race',
  quiz: 'Quiz',
  connect: 'Connect',
  decode: 'Decode',
  rts: 'Strategy',
};

const CATEGORY_LABEL: Record<string, string> = {
  games: 'Games',
  lobbies: 'Lobbies',
  interests: 'Interests',
  dating: 'Dating',
};

function worldName(room: Pick<NewsRoom, 'name' | 'templateName'>): string {
  const raw = room.templateName?.trim() || room.name.trim();
  return raw.length > 28 ? `${raw.slice(0, 27).trimEnd()}…` : raw;
}

function activityPhrase(room: Pick<NewsRoom, 'gameKind' | 'category'>, world: string): string {
  const kind = room.gameKind?.toLowerCase();
  if (kind && KIND_PHRASE[kind]) return KIND_PHRASE[kind](world);
  const cat = (room.category ?? 'lobbies').toLowerCase();
  return (CATEGORY_PHRASE[cat] ?? CATEGORY_PHRASE.lobbies)(world);
}

export function kindOrCategoryLabel(room: Pick<NewsRoom, 'gameKind' | 'category'>): string {
  const kind = room.gameKind?.toLowerCase();
  if (kind && KIND_LABEL[kind]) return KIND_LABEL[kind];
  const cat = (room.category ?? 'lobbies').toLowerCase();
  return CATEGORY_LABEL[cat] ?? 'Lobbies';
}

/** First name-shaped token of a (possibly already-initialed) display name — never re-derives past what the server already gated. */
export function firstNameOf(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

export interface PeopleAnchor {
  /** The occupant the headline/people-row leads with (human preferred; NPC greeter otherwise). */
  anchor: ExploreOccupant | null;
  humans: ExploreOccupant[];
  /** Humans beyond the anchor ("+N others"). */
  othersCount: number;
}

export function resolveAnchor(occupants: ExploreOccupant[] | undefined): PeopleAnchor {
  const list = occupants ?? [];
  const humans = list.filter((o) => !o.isNpc);
  const npcs = list.filter((o) => o.isNpc);
  const anchor = humans[0] ?? npcs[0] ?? null;
  return { anchor, humans, othersCount: Math.max(0, humans.length - 1) };
}

/**
 * R3 headline: "{firstName} +N others are {kindVerb} in {worldShort}", a
 * kind-specific verb table, or the "Open table" invitation fallback for a
 * truly empty room. Classroom skin uses the lesson title verbatim (room.name)
 * per the school model's visual-compatibility constraint (§7) — the
 * who's-doing-what template is a dating/social-scope idiom.
 */
export function buildHeadline(room: NewsRoom, opts?: { isClassroom?: boolean }): string {
  if (opts?.isClassroom) return room.name;

  const world = worldName(room);
  const { anchor, humans, othersCount } = resolveAnchor(room.occupants);

  if (!anchor) return `Open table at ${world}`;

  if (humans.length === 0) {
    // NPC-only room — empty-room honesty (design §4): the greeter is named, never anonymized further.
    return `${firstNameOf(anchor.displayName)} is hosting in ${world}`;
  }

  const subject = othersCount > 0 ? `${firstNameOf(anchor.displayName)} +${othersCount} others` : firstNameOf(anchor.displayName);
  const verb = othersCount > 0 ? 'are' : 'is';
  return `${subject} ${verb} ${activityPhrase(room, world)}`;
}

/**
 * R2 byline: "{world} · {kind} · {state}", small/quiet. Classroom skin swaps
 * the teacher into the *people row* (school-lessons.md §7), not the byline —
 * there is no real curriculum-subject/grade field yet, so world+kind stands
 * in for "subject" honestly rather than fabricating one, and the byline
 * grammar is identical across skins.
 */
export function buildByline(room: NewsRoom): string {
  const world = worldName(room);
  const kind = kindOrCategoryLabel(room);
  const state = roomStateText(room);
  return `${world} · ${kind} · ${state}`;
}

function roomStateText(room: Pick<NewsRoom, 'humanCount' | 'maxPlayers' | 'createdAt'>): string {
  if (room.humanCount <= 0) return 'waiting for the first guest';
  if (room.maxPlayers > 0 && room.humanCount >= room.maxPlayers) return 'full';
  return `started ${timeAgo(room.createdAt)} ago`;
}

/** Compact "Xm"/"Xh"/"Xd" relative time — no external date lib, seeded/live rooms only need coarse precision. */
export function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'moments';
  const diffMs = Date.now() - then;
  const minutes = Math.max(0, Math.round(diffMs / 60000));
  if (minutes < 1) return 'moments';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

/**
 * R2 relevance line (purple, only if real): prefers WU-05's own reason text
 * when the room came from the ranked seam; otherwise falls back to the
 * shared-interest signal already available from the peek-visibility
 * projection (R5) — "N people here share your interests". Never fabricated.
 */
export function buildRelevanceLine(room: NewsRoom): string | null {
  const ranked = room as Partial<RankedRoom>;
  if (ranked.relevanceReasons && ranked.relevanceReasons.length > 0) {
    return ranked.relevanceReasons[0];
  }
  const sharedCount = (room.occupants ?? []).filter((o) => (o.sharedInterestCount ?? 0) > 0).length;
  if (sharedCount > 0) {
    return `${sharedCount} ${sharedCount === 1 ? 'person' : 'people'} here share your interests`;
  }
  return null;
}

/** The "For you" empty-room invitation micro-copy (design §4) — never shown in Hero/Live-now. */
export function emptyRoomInvitation(room: NewsRoom): string | null {
  if (room.humanCount > 0) return null;
  const { anchor, humans } = resolveAnchor(room.occupants);
  if (anchor && humans.length === 0) {
    return `${firstNameOf(anchor.displayName)} is hosting — they'll keep you company till someone joins.`;
  }
  return 'Be the one who starts it.';
}

export interface NewsSections<T> {
  hero: T | null;
  liveNow: T[];
  forYou: T[];
}

/**
 * R7 empty-room honesty: rooms with 0 humans never land in Hero/Live-now.
 * `rankedRooms` is assumed already ordered (WU-05 relevance, or the
 * occupancy-fallback sort) — this only partitions, it never re-sorts.
 */
export function splitNewsSections<T extends { lobbyId: string; humanCount: number }>(
  rankedRooms: T[],
  opts?: { liveNowSize?: number; forYouSize?: number },
): NewsSections<T> {
  const liveNowSize = opts?.liveNowSize ?? 4;
  const forYouSize = opts?.forYouSize ?? 4;

  const live = rankedRooms.filter((r) => r.humanCount > 0);
  const hero = live[0] ?? null;
  const liveNow = live.slice(1, 1 + liveNowSize);

  const used = new Set([hero, ...liveNow].filter((r): r is T => r !== null).map((r) => r.lobbyId));
  const forYou = rankedRooms.filter((r) => !used.has(r.lobbyId)).slice(0, forYouSize);

  return { hero, liveNow, forYou };
}

/** ≤30min-to-start "starting soon" chip (design §2, the sanctioned orange focus-enhancer use). */
export function isStartingSoon(startsAtUtc: string): boolean {
  const diffMs = new Date(startsAtUtc).getTime() - Date.now();
  return diffMs >= 0 && diffMs <= 30 * 60 * 1000;
}

export function hasStarted(startsAtUtc: string): boolean {
  return new Date(startsAtUtc).getTime() <= Date.now();
}
